import { ProctoringOption } from "@verity/proctoring-option/types";
import styles from "./index.module.css";
import { CategoryOptionsListProps } from "../proctoringOptionsGrid";
import { CoreInfoIndicator, RadioOption } from "../../../core";
import { Typography } from "@mui/material";
import i18nPackage from "../../../i18n/i18n";
import { shouldShowProctoringOption } from "../configurationSummaryList";
import OptionBlock from "../optionBlock";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import { RadioGroupSubItem } from "../option-menus/radioGroupSubItem";

const ScientificRadioOption = () => {
	return (
		<div className={styles.scientificContainer}>
			<Typography>{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientific")}</Typography>
			<CoreInfoIndicator
				hint={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientificTooltip")}
				size={20}
			/>
		</div>
	);
};

export default function ShowToolsOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation = false,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T>) {
	const { enableCalculator, enableWhiteboard } = option;

	const calculatorAllowed = isOptionAllowed("enableCalculator");
	const whiteboardAllowed = isOptionAllowed("enableWhiteboard");

	const calcRadioOptions: RadioOption[] = [
		{
			value: "standard",
			label: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.standard"),
			disabled: !calculatorAllowed,
		},
		{
			value: "scientific",
			label: <ScientificRadioOption />,
			disabled: !calculatorAllowed,
		},
	];

	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowProctoringOption("enableCalculator") && (
				<OptionBlock
					checked={enableCalculator !== "off"}
					updateState={(checked) => {
						updateOption({
							...option,
							enableCalculator: checked ? "standard" : "off",
							showRecordingPanel: checked ? true : option.showRecordingPanel,
						});
					}}
					name="enableCalculator"
					description={PROCTORING_OPTION_TEXT.enableCalculator.description}
					Icon={PROCTORING_OPTION_TEXT.enableCalculator.icon}
					contentHeader={PROCTORING_OPTION_TEXT.enableCalculator.header}
					disabled={!calculatorAllowed}
					fullDisabled={!isOptionAllowed("enableCalculator") || disabled}
					disabledMessage={
						!calculatorAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(
									option,
									"enableCalculator",
									enableCalculator === "off" ? "standard" : "off",
									enableCalculator !== "off",
								)
					}
					horizontalOrientation={horizontalOrientation}
					menuItems={
						<RadioGroupSubItem
							radioGroupDescription={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.subtitle")}
							radioOptions={calcRadioOptions}
							onChange={(_, value) => {
								updateOption({
									...option,
									enableCalculator: value,
								});
							}}
							selectedValue={enableCalculator}
						/>
					}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("enableCalculator")}
				/>
			)}
			{shouldShowProctoringOption("enableWhiteboard") && (
				<OptionBlock
					name="enableWhiteboard"
					checked={Boolean(enableWhiteboard)}
					updateState={(checked) => {
						updateOption({
							...option,
							enableWhiteboard: checked,
							showRecordingPanel: checked ? true : option.showRecordingPanel,
						});
					}}
					description={PROCTORING_OPTION_TEXT.enableWhiteboard.description}
					Icon={PROCTORING_OPTION_TEXT.enableWhiteboard.icon}
					contentHeader={PROCTORING_OPTION_TEXT.enableWhiteboard.header}
					disabled={!whiteboardAllowed}
					fullDisabled={disabled || !whiteboardAllowed}
					disabledMessage={
						!whiteboardAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(option, "enableWhiteboard", !enableWhiteboard, Boolean(enableWhiteboard))
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("enableWhiteboard")}
				/>
			)}
		</div>
	);
}
